import React, { useState } from 'react';
import { Building2, Plus, Pencil, Trash2, Star, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import { saasCreateCompany, saasUpdateCompany, saasDeleteCompany, saasSetDefaultCompany } from '../../api';

const EMPTY = { name: '', trn: '', address: '', email: '', phone: '' };

const Field = ({ k, label, ph, type = 'text', form, setForm }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest text-[#111827]/50 dark:text-slate-400 font-bold">{label}</label>
    <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="h-10 mt-1 bg-white dark:bg-slate-800" data-testid={`company-form-${k}`} />
  </div>
);

const CompanyForm = ({ lang, form, setForm }) => {
  const ar = lang === 'ar';
  const p = { form, setForm };
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className="sm:col-span-2"><Field {...p} k="name" label={ar ? 'اسم الشركة *' : 'Company name *'} ph={ar ? 'مثال: شركة النور للتجارة' : 'e.g. Al Noor Trading LLC'} /></div>
      <Field {...p} k="trn" label={ar ? 'الرقم الضريبي (TRN)' : 'Tax Registration No. (TRN)'} ph="100XXXXXXXXXXXX" />
      <Field {...p} k="phone" label={ar ? 'الهاتف' : 'Phone'} ph="+971 50 000 0000" />
      <Field {...p} k="email" label={ar ? 'البريد الإلكتروني' : 'Email'} ph="finance@company.ae" type="email" />
      <Field {...p} k="address" label={ar ? 'العنوان' : 'Address'} ph={ar ? 'دبي، الإمارات' : 'Dubai, UAE'} />
    </div>
  );
};

export const CompaniesManager = ({ lang, data, onChanged, planLabel }) => {
  const ar = lang === 'ar';
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const companies = data?.companies || [];
  const max = data?.max_companies ?? 1;
  const unlimited = max >= 1e8;

  const startAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const startEdit = (c) => { setEditing(c); setForm({ name: c.name, trn: c.trn || '', address: c.address || '', email: c.email || '', phone: c.phone || '' }); setOpen(true); };

  const save = async () => {
    if (form.name.trim().length < 2) { toast({ title: ar ? 'اسم الشركة مطلوب' : 'Company name is required', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      if (editing) await saasUpdateCompany(editing.id, form); else await saasCreateCompany(form);
      toast({ title: editing ? (ar ? 'تم تحديث بيانات الشركة' : 'Company updated') : (ar ? 'تمت إضافة الشركة' : 'Company added') });
      setOpen(false);
      await onChanged();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(ar ? `حذف شركة "${c.name}"؟` : `Delete company "${c.name}"?`)) return;
    try { await saasDeleteCompany(c.id); toast({ title: ar ? 'تم الحذف' : 'Deleted' }); await onChanged(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const makeDefault = async (c) => {
    try { await saasSetDefaultCompany(c.id); await onChanged(); }
    catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  return (
    <div className="glass-card rounded-xl p-5 sm:p-6 mb-6" data-testid="companies-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg text-[#0a0a0a] dark:text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-[#2563eb]" /> {ar ? 'الشركات' : 'Companies'}</h3>
          <p className="text-xs text-[#111827]/55 dark:text-slate-400 mt-0.5" data-testid="companies-limit">
            {ar ? `باقتك (${planLabel}) تسمح بـ ${unlimited ? 'عدد غير محدود من الشركات' : `${max} ${max === 1 ? 'شركة' : 'شركات'}`} · المستخدم: ${companies.length}`
                : `Your ${planLabel} plan allows ${unlimited ? 'unlimited companies' : `${max} compan${max === 1 ? 'y' : 'ies'}`} · using ${companies.length}`}
          </p>
        </div>
        {data?.can_add ? (
          <Button onClick={startAdd} className="gold-button rounded-md h-9 px-4 text-xs" data-testid="company-add-btn"><Plus className="w-3.5 h-3.5 me-1" /> {ar ? 'إضافة شركة' : 'Add company'}</Button>
        ) : (
          <a href="/accounting/pricing" className="inline-flex items-center gap-1 text-xs text-[#2563eb] hover:underline" data-testid="company-upgrade-link"><Lock className="w-3 h-3" /> {ar ? 'ترقية الباقة لإضافة شركات' : 'Upgrade plan to add more'}</a>
        )}
      </div>

      <div className="divide-y divide-[#e5e7eb] dark:divide-slate-700">
        {companies.map((c) => (
          <div key={c.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`company-row-${c.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-[#0a0a0a] dark:text-white" data-testid={`company-name-${c.id}`}>{c.name}</span>
                {c.is_default && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-bold uppercase tracking-wider">{ar ? 'افتراضية' : 'Default'}</span>}
              </div>
              <div className="text-[11px] text-[#111827]/55 dark:text-slate-400 mt-0.5 flex flex-wrap gap-x-3">
                {c.trn && <span>TRN {c.trn}</span>}
                {c.email && <span>{c.email}</span>}
                {c.phone && <span dir="ltr">{c.phone}</span>}
                {c.address && <span>{c.address}</span>}
                <span>{c.txn_count || 0} {ar ? 'عملية' : 'txns'}</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {!c.is_default && <Button onClick={() => makeDefault(c)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" title={ar ? 'تعيين كافتراضية' : 'Set as default'} data-testid={`company-default-${c.id}`}><Star className="w-3.5 h-3.5" /></Button>}
              <Button onClick={() => startEdit(c)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent" data-testid={`company-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              {companies.length > 1 && <Button onClick={() => remove(c)} className="ghost-button rounded-md h-8 px-2 text-xs bg-transparent text-rose-600" data-testid={`company-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="company-dialog">
          <DialogHeader><DialogTitle>{editing ? (ar ? 'تعديل بيانات الشركة' : 'Edit company') : (ar ? 'إضافة شركة جديدة' : 'Add a new company')}</DialogTitle></DialogHeader>
          <CompanyForm lang={lang} form={form} setForm={setForm} />
          <DialogFooter>
            <Button onClick={() => setOpen(false)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">{ar ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={save} disabled={busy} className="gold-button rounded-md h-10 px-5 text-xs" data-testid="company-save-btn">{busy ? '…' : (ar ? 'حفظ' : 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
